// @owners { team: patients }
import React, { useEffect, useState } from 'react';
import { Linking, Platform, StyleSheet } from 'react-native';
import { type WebViewMessageEvent } from 'react-native-webview';
import { type ShouldStartLoadRequest, type WebViewNavigation } from 'react-native-webview/lib/WebViewTypes';
import { wrapHtmlContent } from '../../coupons/helpers/wrapHtmlContent';
import { WebView } from './WebView';
import { handleTermsWebViewMessage } from './handleTermsWebViewMessage';
import { WEB_VIEW_INITIAL_HEIGHT, WEB_VIEW_MOBILE_MAX_HEIGHT, WEB_VIEW_WEB_MAX_HEIGHT } from './termsWebViewConstants';

const TERMS_WEB_VIEW_LINK_INJECTED_JAVASCRIPT = `
  document.addEventListener("click", function(event) {
    let target = event.target.closest("a");
    if (target && target.href) {
      event.preventDefault();
      window.open(target.href, '_blank', 'noopener,noreferrer');
    }
  });
`;

const styles = StyleSheet.create({
  webViewContainer: { flex: 0, flexGrow: 0, flexShrink: 0 },
  webViewWebContainer: { flexShrink: 0, height: WEB_VIEW_WEB_MAX_HEIGHT, overflow: 'hidden' },
});

type TermsWebViewProps = {
  htmlContent: string;
  onScrolledToBottom?: () => void;
};

export const TermsWebView = ({ htmlContent, onScrolledToBottom }: TermsWebViewProps) => {
  const isWeb = Platform.OS === 'web';
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  // Reset measured height when terms change (adjust during render — no Effect needed).
  const [prevHtmlContent, setPrevHtmlContent] = useState(htmlContent);
  if (prevHtmlContent !== htmlContent) {
    setPrevHtmlContent(htmlContent);
    setContentHeight(null);
  }

  const handleWebViewMessage = (event: WebViewMessageEvent) => {
    handleTermsWebViewMessage({
      data: event.nativeEvent.data,
      isWeb: false,
      onContentHeight: setContentHeight,
      onScrolledToBottom,
    });
  };

  // Web: listen for postMessage from the srcdoc iframe. Origin is validated in the helper;
  // do not add targetOrigin in iframe script (about:srcdoc origin is "null").
  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const messageHandler = (event: MessageEvent) => {
      handleTermsWebViewMessage({
        data: event.data as string,
        expectedOrigin: window.location.origin,
        isWeb: true,
        onScrolledToBottom,
        origin: event.origin,
      });
    };

    // eslint-disable-next-line sonarjs/post-message -- handleTermsWebViewMessage checks event.origin against expectedOrigin
    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [isWeb, onScrolledToBottom]);

  const handleNavigation = (event: ShouldStartLoadRequest | WebViewNavigation) => {
    const { url } = event;
    if (url.startsWith('about:')) {
      return true;
    }

    Linking.openURL(url);
    return false;
  };

  const webViewHeight = isWeb
    ? WEB_VIEW_WEB_MAX_HEIGHT
    : Math.min(contentHeight ?? WEB_VIEW_INITIAL_HEIGHT, WEB_VIEW_MOBILE_MAX_HEIGHT);

  return (
    <WebView
      containerStyle={isWeb ? styles.webViewWebContainer : styles.webViewContainer}
      injectedJavaScript={isWeb ? TERMS_WEB_VIEW_LINK_INJECTED_JAVASCRIPT : undefined}
      onMessage={!isWeb ? handleWebViewMessage : undefined}
      onShouldStartLoadWithRequest={!isWeb ? handleNavigation : undefined}
      originWhitelist={['*']}
      scrollEnabled={isWeb ? undefined : contentHeight === null || contentHeight > WEB_VIEW_MOBILE_MAX_HEIGHT}
      source={{ html: wrapHtmlContent(htmlContent) }}
      style={{ height: webViewHeight }}
    />
  );
};
